import {
  ChevronDown,
  ChevronUp,
  LogOut,
  User,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { resetState } from "../../reducers/staticData";
import { UserState } from "../../reducers/userSlice";
import { decrypt, getSessionStorage } from "../../utils";
import { logoutUser } from "../../api/common";
import "./style.scss";
import Home from "../../icons/Auditm.svg";
import Calendar from "../../icons/AuditPlanning.svg";
import Activity from "../../icons/AIPS.svg";
import Clock from "../../icons/policym.svg";
import CheckCircle2 from "../../icons/Closure b.svg";
import Inreview from "../../icons/configm.svg";
import { FaAngleDoubleLeft } from "react-icons/fa";
import { FaAngleDoubleRight } from "react-icons/fa";
import A360_logo from "../../icons/A360withoutsubLine.png";
import A360 from "../../icons/A360logo.svg";

interface HeaderProps {
  activeStage?: "planning" | "scheduled" | "in-progress" | "closure";
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
  href?: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
  href?: string;
  description?: string;
  size?: { width: number; height: number };
  diamond?: boolean;
  childNav?: NavItem[];
}

const Header: React.FC<HeaderProps> = () => {
  const [userInfo, setUserInfo] = useState<UserState>();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "audit-management",
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const token = getSessionStorage("token");

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      sessionStorage.clear();
      dispatch(resetState());
      navigate("/login");
    }
  };

  useEffect(() => {
    if (getSessionStorage("user")) {
      setUserInfo(JSON.parse(decrypt(getSessionStorage("user"))));
    }
  }, []);
  useEffect(() => {
    const currentPath = location.pathname;
    const childExpanded: string[] = [];

    const activeSection = navigationSections.find((section) =>
      section.items.some((item) => {
        if (item.href && currentPath.startsWith(item.href)) return true;
        if (item.childNav?.some((child) => child.href && currentPath.startsWith(child.href))) {
          childExpanded.push(item.id); // expand parent item (e.g. "settings")
          return true;
        }
        return false;
      }),
    );

    if (activeSection) {
      // Only the active section + its child parents, no other top-level sections
      setExpandedSections([activeSection.id, ...childExpanded]);
    }
  }, [location.pathname]);

  const navigationSections: NavSection[] = [
    {
      id: "audit-management",
      label: "Audit Management",
      items: [
        {
          id: "audit-overview",
          label: "Audit Overview",
          icon: Home,
          href: "/overview/audit-overview",
          description: "Dashboards",
        },
        {
          id: "home",
          label: "Audit Insights",
          icon: Home,
          href: "/home",
          description: "Dashboards",
        },
        {
          id: "audit-analytics",
          label: "Audit Analytics",
          icon: Home,
          href: "/audit-analytics",
          description: "Dashboards",
        },
        {
          id: "Overview",
          label: "Audit Lists",
          icon: Inreview,
          href: "/audit-overview",
          description: "Plan and prepare audit activities",
        },
        {
          id: "planning",
          label: "Audit Scheduled",
          icon: Calendar,
          href: "/audit-scheduled",
          description: "Plan and prepare audit activities",
        },
        {
          id: "in-progress",
          label: "Audit In Progress",
          icon: Activity,
          href: "/in-progress",
          description: "Monitor ongoing audits",
          size: { width: 18, height: 18 },
        },
        {
          id: "closure",
          label: "Audit Closure",
          icon: CheckCircle2,
          href: "/closure",
          description: "Complete and close audits",
        },
      ],
    },
    {
      id: "policy-management",
      label: "Policy Management",
      items: [
        {
          id: "policy-overview",
          label: "Policy Overview",
          icon: Home,
          href: "/policy-overview/ComingSoon",
          description: "Dashboards",
        },
        {
          id: "policy",
          label: "Policy Insights",
          icon: Home,
          href: "/policy-insight/ComingSoon",
          description: "Dashboards",
        },
        {
          id: "policy-comparison",
          label: "Policy Comparison",
          icon: Clock,
          href: "/policy-comparison",
          description: "Compare policies",
        },
      ],
    },
    {
      id: "configuration",
      label: "Configuration",
      items: [
        {
          id: "settings",
          label: "Settings",
          icon: Home,
          description: "Application Settings",
          childNav: [
            {
              id: "ldap-config",
              label: "LDAP Configuration",
              icon: Home,
              href: "/settings/ldap",
              description: "LDAP Settings",
              diamond: true,
            },
            {
              id: "LLM Configuration",
              label: "LLM Configuration",
               href: "/settings/llm-Configuration",
               icon: Home,
               description : "Internl and external Configuration",
               diamond: true,
            },
            {
          id: "ai-interaction-configuration",
          label: "AI Interaction Configuration",
          icon: Home,
          href: "/settings/aiinteractionconfiguration",
          description: "Configure AI interactions",
          diamond: true,
        }


          ],
        },
        {
          id: "Administration",
          label: "Administration",
          icon: Home,
          description: "Administration",
          childNav: [
            {
              id: "user-management",
              label: "User Management",
              icon: Clock,
              href: "/administration/usermanagement",
              description: "Manage users",
              diamond: true,
            },
          ],
        },
      ],
    },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const sectionIds = ["audit-management", "policy-management", "configuration"];

  const toggleSection = (sectionId: string, sectionHref?: string) => {
    // If section has href, navigate to it
    if (sectionHref) {
      navigate(sectionHref);
      return;
    }

    // If sidebar is collapsed, expand it and show the section
    if (isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
      // Collapse other top-level sections, keep only the clicked one
      setExpandedSections((prev) => {
        const childIds = prev.filter((id) => !sectionIds.includes(id));
        return [sectionId, ...childIds];
      });
      return;
    }

    const isTopLevel = sectionIds.includes(sectionId);

    if (isTopLevel) {
      setExpandedSections((prev) => {
        const isAlreadyOpen = prev.includes(sectionId);
        // Keep child-level expanded items (e.g. "settings", "Administration")
        const childIds = prev.filter((id) => !sectionIds.includes(id));
        return isAlreadyOpen ? [...childIds] : [sectionId, ...childIds];
      });
    } else {
      // Toggle child sections (e.g. "settings", "Administration")
      setExpandedSections((prev) =>
        prev.includes(sectionId)
          ? prev.filter((id) => id !== sectionId)
          : [...prev, sectionId]
      );
    }
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault();
    navigate(href);
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    const names = name.split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const sectionIcons: Record<string, any> = {
    "audit-management": Home,
    "policy-management": Clock,
    configuration: Inreview,
  };

  return (
    <>
      <aside className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          {!isSidebarCollapsed ? (
            <img src={A360_logo} alt="A360 Logo" className="sidebar-logo" />
          ) : (
            <div className="sidebar-logo-icon">
              <img src={A360} alt="A360 Logo" className="sidebar-logo" />
            </div>
          )}
        </div>

        {/* Navigation Menu with Sections */}
        <nav
          className="sidebar-nav"
          role="navigation"
          aria-label="Main navigation"
        >
          {navigationSections.map((section) => {
            const isSectionExpanded = expandedSections.includes(section.id);
            const isSectionActive =
              section.items.some((item) => {
                if (item.href && location.pathname.startsWith(item.href)) return true;
                if (item.childNav?.some((child) => child.href && location.pathname.startsWith(child.href))) return true;
                return false;
              }) ||
              (section.href && location.pathname.startsWith(section.href));

            return (
              <div key={section.id} className="sidebar-section">
                <button
                  className={`sidebar-section-header ${isSectionActive ? "active" : ""}`}
                  onClick={() => toggleSection(section.id, section.href)}
                  aria-expanded={isSectionExpanded}
                >
                  {/* Section Icon Visible Always */}
                  {sectionIcons[section.id] && (
                    <>
                      {typeof sectionIcons[section.id] === "string" ? (
                        <img
                          src={sectionIcons[section.id]}
                          className="sidebar-section-icon"
                          alt={section.label}
                        />
                      ) : (
                        React.createElement(sectionIcons[section.id], {
                          className: "sidebar-section-icon",
                        })
                      )}
                    </>
                  )}
                  {!isSidebarCollapsed && (
                    <span className="sidebar-section-title">
                      {section.label}
                    </span>
                  )}
                </button>

                {/* Section Items with Timeline */}
                <ul
                  className={`sidebar-section-items ${!isSidebarCollapsed && isSectionExpanded
                    ? "expanded"
                    : "collapsed"
                    }`}
                >
                  {section.items.map((item, index) => {
                    const hasChildren = item.childNav && item.childNav.length > 0;
                    const isExpanded = expandedSections.includes(item.id);
                    const isActive = item.href ? location.pathname.startsWith(item.href) : false;
                    const isLastItem = index === section.items.length - 1;

                    return (
                      <React.Fragment key={item.id}>
                        {!isSidebarCollapsed && (
                          <>
                            <li className="sidebar-item">
                              {hasChildren ? (
                                <button
                                  className="sidebar-link parent-item"
                                  onClick={() => toggleSection(item.id)}
                                  title={item.label}
                                >
                                  <div className="timeline-wrapper">
                                    <div className="timeline-dot" />
                                    {(isExpanded || !isLastItem) && <div className="timeline-line-nav" />}
                                  </div>
                                  <span className="sidebar-label">{item.label}</span>
                                </button>
                              ) : (
                                <NavLink
                                  to={item.href!}
                                  className={({ isActive }) =>
                                    isActive ? "sidebar-link active" : "sidebar-link"
                                  }
                                  onClick={(e) => handleNavClick(e, item.href!)}
                                  title={item.label}
                                >
                                  <div className="timeline-wrapper">
                                    <div className={`timeline-dot ${item.diamond ? "diamond" : ""} ${isActive ? "active" : ""}`} />
                                    {!isLastItem && <div className="timeline-line-nav" />}
                                  </div>
                                  <span className="sidebar-label">{item.label}</span>
                                </NavLink>
                              )}
                            </li>
                            {/* Child items — same toggleSection mechanism, diamond dot */}
                            {hasChildren && isExpanded && item.childNav!.map((child, childIndex) => {
                              const isChildActive = child.href ? location.pathname.startsWith(child.href) : false;
                              const isLastChild = childIndex === item.childNav!.length - 1;
                              return (
                                <li key={child.id} className="sidebar-item">
                                  <NavLink
                                    to={child.href!}
                                    className={({ isActive }) =>
                                      isActive ? "sidebar-link active" : "sidebar-link"
                                    }
                                    onClick={(e) => handleNavClick(e, child.href!)}
                                    title={child.label}
                                  >
                                    <div className="timeline-wrapper">
                                      <div className={`timeline-dot ${child.diamond ? "diamond" : ""} ${isChildActive ? "active" : ""}`} />
                                      {!(isLastChild && isLastItem) && <div className="timeline-line-nav" />}
                                    </div>
                                    <span className="sidebar-label">{child.label}</span>
                                  </NavLink>
                                </li>
                              );
                            })}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>
        {/* USER SECTION MOVED TO BOTTOM OF SIDEBAR */}
        <div
          className={`sidebar-user-section ${isSidebarCollapsed ? "collapsed" : ""}`}
          ref={dropdownRef}
        >
          {/* COLLAPSED → avatar only with dropdown */}
          {isSidebarCollapsed ? (
            <>
              <div className="sidebar-user-avatar" role="button" tabIndex={0} onClick={toggleUserDropdown}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleUserDropdown(); } }}
              >
                <span className="user-initials">
                  {getInitials(userInfo?.name || "")}
                </span>
              </div>
              {isUserDropdownOpen && (
                <div className="sidebar-dropdown-collapsed open">
                  <div className="sidebar-dropdown-header">
                    <div className="sidebar-dropdown-user-info">
                      <div className="sidebar-dropdown-user-details">
                        <h4>{userInfo?.name}</h4>
                        <p>{userInfo?.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="sidebar-dropdown-content">
                    <button
                      className="sidebar-dropdown-item danger"
                      onClick={logout}
                    >
                      <LogOut className="dropdown-icon" /> Logout
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* EXPANDED → full trigger + dropdown */}
              <button
                className={`sidebar-user-trigger ${isUserDropdownOpen ? "open" : ""}`}
                onClick={toggleUserDropdown}
              >
                <div className="sidebar-user-avatar">
                  <span className="user-initials">
                    {getInitials(userInfo?.name || "")}
                  </span>
                </div>

                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{userInfo?.name}</span>
                  <span className="sidebar-user-role">Lead Auditor</span>
                </div>

                <ChevronDown className="user-dropdown-arrow" />
              </button>

              {isUserDropdownOpen && (
                <div className="sidebar-dropdown-menu open">
                  <div className="sidebar-dropdown-header">
                    <div className="sidebar-dropdown-user-info">
                      <div className="sidebar-dropdown-user-details">
                        <h4>{userInfo?.name}</h4>
                        <p>{userInfo?.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="sidebar-dropdown-content">
                    <button
                      className="sidebar-dropdown-item danger"
                      onClick={logout}
                    >
                      <LogOut className="dropdown-icon" /> Logout
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <button
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={
            isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
          }
        >
          {isSidebarCollapsed ? (
            <FaAngleDoubleRight size={25} color={"#fff"} />
          ) : (
            <FaAngleDoubleLeft size={25} color={"#fff"} />
          )}
        </button>
      </aside>
    </>
  );
};

export default Header;
